import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminListShell } from "@/components/admin/AdminListShell";
import { siteSettingsQuery, INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { updateSiteSettings } from "@/lib/site-settings.functions";

export const Route = createFileRoute("/admin/settings/")({
  head: () => ({
    meta: [
      { title: "Settings · Admin · Shugert" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SettingsForm,
});

function SettingsForm() {
  const router = useRouter();
  const query = useQuery(siteSettingsQuery());
  const [home, setHome] = useState<number>(6);
  const [work, setWork] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data?.settings) {
      setHome(query.data.settings.home_projects_limit);
      setWork(query.data.settings.work_projects_limit);
    }
  }, [query.data]);

  const mutation = useAuthedMutation(updateSiteSettings, {
    invalidate: [INVALIDATE.siteSettings, INVALIDATE.publicProjects],
    invalidateOn: "settled",
    onSuccess: () => {
      setSaved(true);
      setError(null);
      router.invalidate();
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => {
      setError(err.message || "Failed to save");
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    mutation.mutate({
      home_projects_limit: Number.isFinite(home) ? home : 6,
      work_projects_limit: Number.isFinite(work) ? work : 0,
    });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
          Settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Control public content limits, colors, Home details, report branding and AI providers.
        </p>
      </header>

      <nav className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Link
          to="/admin/settings/theme"
          className="rounded-2xl border border-border bg-card/30 p-5 transition-colors hover:border-foreground/50"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Theme</div>
          <div className="mt-1 font-display text-base font-semibold text-foreground">
            Colors &amp; backgrounds
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Semantic colors, gradients, surfaces, money-page recipes and dark rails.
          </p>
        </Link>
        <Link
          to="/admin/settings/home-visuals"
          className="rounded-2xl border border-blue-500/25 bg-blue-500/[0.04] p-5 transition-colors hover:border-blue-500/50"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-blue-700">Home details</div>
          <div className="mt-1 font-display text-base font-semibold text-foreground">
            Rules, clients &amp; press
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Exact controls for the line below navigation, client heading, press section and final CTA.
          </p>
        </Link>
        <Link
          to="/admin/settings/ai"
          className="rounded-2xl border border-border bg-card/30 p-5 transition-colors hover:border-foreground/50"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">AI</div>
          <div className="mt-1 font-display text-base font-semibold text-foreground">
            Provider &amp; models
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Choose which model powers the audit pipeline.
          </p>
        </Link>
        <Link
          to="/admin/settings/report"
          className="rounded-2xl border border-border bg-card/30 p-5 transition-colors hover:border-foreground/50"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Reports
          </div>
          <div className="mt-1 font-display text-base font-semibold text-foreground">
            Delivery &amp; branding
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Signature, header, footer, contact info, confidentiality.
          </p>
        </Link>
      </nav>

      <div className="rounded-2xl border border-border bg-card/30">
        <AdminListShell query={query} skeletonRows={2}>
          {() => (
            <form onSubmit={onSubmit} className="space-y-6 p-6">
              <div className="space-y-2">
                <label htmlFor="home-limit" className="block text-sm font-medium text-foreground">
                  Projects on homepage
                </label>
                <input
                  id="home-limit"
                  type="number"
                  min={1}
                  max={50}
                  value={home}
                  onChange={(e) => setHome(parseInt(e.target.value, 10) || 0)}
                  className="w-32 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground">
                  Total count, including the featured project.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="work-limit" className="block text-sm font-medium text-foreground">
                  Projects on Work page
                </label>
                <input
                  id="work-limit"
                  type="number"
                  min={0}
                  max={50}
                  value={work}
                  onChange={(e) => setWork(parseInt(e.target.value, 10) || 0)}
                  className="w-32 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground">Set to 0 to show all projects.</p>
              </div>

              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
              {saved && (
                <div className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                  Saved.
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {mutation.isPending ? "Saving…" : "Save settings"}
                </button>
              </div>
            </form>
          )}
        </AdminListShell>
      </div>
    </div>
  );
}
